import { Capacity } from './../../models/capacity.model';
import { CalendarHeaderComponent } from './calendar-header/calendar-header.component';
import { Component, OnInit, Input, ViewChild, Renderer2, ElementRef, forwardRef, ChangeDetectionStrategy, Optional, Host, SkipSelf } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { NG_VALUE_ACCESSOR, ControlContainer, AbstractControl } from '@angular/forms';
import { LuxonDateAdapter } from '../../services/luxon-date-adapter';
import { DateTime } from 'luxon';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  providers: [
    { provide: DateAdapter, useClass: LuxonDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CalendarComponent),
      multi: true
    }
  ],
  styleUrls: ['./calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class CalendarComponent implements OnInit {
  @ViewChild('tdcalendar') calendar: MatCalendar<DateTime>;
  @ViewChild('tdcalendar', { read: ElementRef }) calendarRef: ElementRef;

  @Input() availableDates: any[];
  @Input() dateDetails: Capacity[];
  @Input() startAt; // = '2020-08-20';
  @Input() minDate; // = '2020-07-20';
  @Input() maxDate; // = '2020-09-24';
  @Input() identifier = 'default'; // = '2020-09-24';
  @Input() selectedDate: DateTime;
  customHeader = CalendarHeaderComponent;

  @Input() formControlName: string;

  private control: AbstractControl;

  onChange: any = (value) => { };
  onTouched: any = () => { };

  constructor(@Optional() @Host() @SkipSelf()
  private controlContainer: ControlContainer, private renderer: Renderer2,
  private readonly dateAdapter:DateAdapter<any>,
  private readonly translate: TranslateService) {
this.dateAdapter.setLocale(this.translate.currentLang);
  }

  ngOnInit() {
    this.control = this.controlContainer.control.get(this.formControlName);
  }
  registerOnChange(fn) {
    this.onChange = fn;
  }

  writeValue(value) {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.control.reset();
    }
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  get value(): DateTime {
    return this.control.value;
  }

  set value(value) {
    this.selectedDate = DateTime.fromFormat(value.toString(), 'yyyy-MM-dd');
    if (this.calendar) {
      this.calendar.activeDate = this.selectedDate;
    }
    this.onChange(value);
    this.onTouched();
  }

  ngAfterViewInit(): void {
    this.updateDayStyles();
    this.calendarOpened();
  }

  filterDate = (d: DateTime): boolean => {
    if (this.availableDates) {
      const currentDate = d.toFormat('YYYY-MM-DD');
      return d.diffNow('days').days >= 0 && this.availableDates.filter(t => t.date === currentDate).length > 0;
    }
    return true;
  }

  setDateClass() {
    return;
  }

  dateChange(e) {
    this.control.setValue(e.toFormat('yyyy-MM-dd'));
  }
  show: boolean = false;
  onTool: boolean = false;
  toolTipMessage: string;

  updateDayStyles() {
    // select each date cell inside the calendar control
    const calendarSelector = this.calendarRef.nativeElement.querySelectorAll(`.${this.identifier} .mat-calendar-content`);
    const dayCellsSelector: any = calendarSelector.length > 0 ? calendarSelector[0].querySelectorAll('.mat-calendar-body-cell') : [];

    dayCellsSelector.forEach(y => {
      let found = false;
      const cDate = this.dateDetails.filter(t => DateTime.fromISO(y.getAttribute('aria-label')).hasSame(DateTime.fromISO(t.date), 'day'));
      if (cDate.length > 0) {
        const div = y.querySelectorAll('div.mat-calendar-body-cell-content')[0];
        const spans = y.querySelectorAll('span');
        if (spans.length > 0) {
          const statusSpan = spans[0];
          statusSpan.classList.remove('statusSpan');
          statusSpan.classList.add('statusSpan', `${cDate[0].slot.healthInd}Capacity`);
        }
        else {
          const statusSpan = document.createElement('span');
          statusSpan.classList.add('statusSpan', `${cDate[0].slot.healthInd}Capacity`);
          div.appendChild(statusSpan);
        }
        found = true;
        div.classList.add('customTooltip');
        if (spans.length > 1) {
          const span = spans[1];
          span.innerHTML = this.buildTitle(cDate[0]);
        }
        else {
          const span = document.createElement('span');
          span.innerHTML = this.buildTitle(cDate[0]);
          span.classList.add('d-flex', 'tooltiptext');
          div.appendChild(span);
        }
      }
      if (!found) {
        const div1 = y.querySelectorAll('div')[0];
        div1.classList.remove('customTooltip');
        const spans1 = y.querySelectorAll('span');
        if (spans1.length > 0) {
          spans1.forEach(span => {
            div1.removeChild(span);
          });
        }
      }
    });
  }

  buildTitle(capacity: Capacity) {
    let title = '';
    if (capacity) {
      title = `<div class="d-flex flex-column mx-auto">
      ${capacity.hour ? `<span>Booked Capacity : ${capacity.hour.booked}</span>` : ''}
      ${capacity.hour ? `<span>Remaining Capacity : ${capacity.hour.remaining}</span>` : ''}
      ${capacity.slot ? `<span>Booked Slots : ${capacity.slot.booked}</span>` : ''}
      ${capacity.slot ? `<span>Remaining Slots : ${capacity.slot.remaining}</span>` : ''}
      </div>`;
    }
    return title;
  }

  listeners = [];
  calendarOpened() {
    setTimeout(() => {
      this.listeners.forEach(v => {
        v();
      });

      this.listeners = [];

      const buttons = this.calendarRef.nativeElement.querySelectorAll('mat-calendar .mat-calendar-body-cell, mat-calendar button, mat-calendar .mat-icon-button');

      buttons.forEach(btn => {
        const x = this.renderer.listen(btn, 'click', () => {
          setTimeout(() => {
            this.calendarOpened();
          });
        })

        this.listeners.push(x);
      });
      this.updateDayStyles();
    });
  }

}